import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth, API } from "@/App";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Search, Calendar, User, ArrowRight, Clock, Tag, TrendingUp, BookOpen, Megaphone, ExternalLink, Bookmark } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SaveArticleButton from "@/components/SaveArticleButton";
import { heroImageForCategory } from "@/lib/categoryImages";

/**
 * ArticleLink — routes correctly based on whether the article is
 * externally sourced (RSS) or a TBC original.
 *   - External (has `source_url` starting with http): opens the source in a
 *     new tab with proper rel-noopener security.
 *   - Internal: routes to /news/:articleId which renders NewsArticlePage.
 * Wraps the card children so the entire card is clickable.
 */
function ArticleLink({ article, children, className, testid }) {
  const src = article.source_url;
  const isExternal = typeof src === "string" && /^https?:\/\//i.test(src);
  const commonProps = { className, "data-testid": testid };
  if (isExternal) {
    return (
      <a href={src} target="_blank" rel="noopener noreferrer" {...commonProps}>
        {children}
      </a>
    );
  }
  return (
    <Link to={`/news/${article.article_id}`} {...commonProps}>
      {children}
    </Link>
  );
}


// Sample news/blog articles (fallback if API has no articles)
const SAMPLE_ARTICLES = [
  {
    article_id: "art_1",
    title: "10 Black-Owned Businesses to Support This Month",
    excerpt: "Discover incredible Black-owned businesses making waves in fashion, food, and tech. These entrepreneurs are changing the game.",
    content: "",
    author: "TBC Editorial",
    created_at: "2025-01-10",
    category: "Featured",
    image: "https://images.unsplash.com/photo-1739298061709-cfc57e6b620e?w=800&q=80",
    read_time: "5 min read",
    featured: true
  },
  {
    article_id: "art_2",
    title: "How to Build Generational Wealth Through Business Ownership",
    excerpt: "Expert advice on creating lasting financial legacy for your family through entrepreneurship and smart investing.",
    content: "",
    author: "Marcus Johnson",
    created_at: "2025-01-08",
    category: "Finance",
    image: "https://images.unsplash.com/photo-1621973133111-63649acea55a?w=800&q=80",
    read_time: "8 min read",
    featured: false
  },
  {
    article_id: "art_3",
    title: "Women in Business: Breaking Barriers in 2025",
    excerpt: "Meet the inspiring Black women entrepreneurs who are shattering glass ceilings and building empires.",
    content: "",
    author: "Jasmine Williams",
    created_at: "2025-01-05",
    category: "Spotlight",
    image: "https://images.unsplash.com/photo-1573165706511-3ffde6ef1fe3?w=800&q=80",
    read_time: "6 min read",
    featured: true
  },
  {
    article_id: "art_4",
    title: "The Rise of Black Tech Founders",
    excerpt: "From startups to unicorns: How Black tech entrepreneurs are reshaping Silicon Valley and beyond.",
    content: "",
    author: "David Chen",
    created_at: "2025-01-03",
    category: "Tech",
    image: "https://images.unsplash.com/photo-1689857538296-b6e1a392a91d?w=800&q=80",
    read_time: "7 min read",
    featured: false
  },
  {
    article_id: "art_5",
    title: "Soul Food Revolution: Black Chefs Reimagining Cuisine",
    excerpt: "How a new generation of Black chefs is honoring tradition while pushing culinary boundaries.",
    content: "",
    author: "Angela Davis",
    created_at: "2024-12-28",
    category: "Food",
    image: "https://images.unsplash.com/photo-1592498542930-4254dc73cbb5?w=800&q=80",
    read_time: "5 min read",
    featured: false
  },
  {
    article_id: "art_6",
    title: "Marketing Tips for Small Business Owners",
    excerpt: "Practical strategies to grow your Black-owned business using social media, SEO, and community engagement.",
    content: "",
    author: "TBC Editorial",
    created_at: "2024-12-25",
    category: "Business Tips",
    image: "https://images.unsplash.com/photo-1716399233137-5f18a8e3520d?w=800&q=80",
    read_time: "10 min read",
    featured: false
  }
];

const NEWS_CATEGORIES_FALLBACK = ["All", "Featured", "Culture", "Technology", "News", "Business"];

export default function NewsPage() {
  const { user } = useAuth();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [newsCategories, setNewsCategories] = useState(NEWS_CATEGORIES_FALLBACK);
  const [savedMap, setSavedMap] = useState({});
  // `user` already destructured from useAuth() above.

  useEffect(() => {
    fetchArticles();
    // Fetch article categories dynamically so the chip row stays in sync
    // with backend BUSINESS_CATEGORIES.
    (async () => {
      try {
        const r = await axios.get(`${API}/article-categories`);
        if (Array.isArray(r.data?.categories)) {
          setNewsCategories(["All", ...r.data.categories]);
        }
      } catch {
        // keep fallback
      }
    })();
  }, []);

  // After we have the article list AND user is signed in, batch-fetch
  // which ones are already in Read Later so save buttons render pre-filled.
  useEffect(() => {
    if (!user || articles.length === 0) { setSavedMap({}); return; }
    const ids = articles.map((a) => a.article_id).filter(Boolean).slice(0, 100).join(",");
    if (!ids) return;
    let alive = true;
    axios.get(`${API}/articles/saved/status?ids=${ids}`, { withCredentials: true })
      .then((r) => { if (alive) setSavedMap(r.data.saved || {}); })
      .catch(() => { /* silent — button still works, just starts unsaved */ });
    return () => { alive = false; };
  }, [user, articles]);

  const fetchArticles = async () => {
    try {
      const response = await axios.get(`${API}/articles`);
      const apiArticles = response.data.articles || [];
      // Use API articles if available, otherwise use sample articles
      setArticles(apiArticles.length > 0 ? apiArticles : SAMPLE_ARTICLES);
    } catch {
      setArticles(SAMPLE_ARTICLES);
    } finally {
      setLoading(false);
    }
  };

  const filteredArticles = articles.filter(article => {
    const matchesSearch = article.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          article.excerpt.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === "All" || article.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const featuredArticles = filteredArticles.filter(a => a.featured);
  const regularArticles = filteredArticles.filter(a => !a.featured);

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  };

  // Helper to get date field (API uses created_at, sample uses created_at)
  const getDate = (article) => article.created_at || article.date;
  const getReadTime = (article) => article.read_time || article.readTime;

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Section */}
      <section className="bg-tbc-cream py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <span className="inline-block px-4 py-2 bg-tbc-gold/20 text-tbc-navy font-medium text-sm tracking-wide rounded-full mb-4">
              TBC INSIGHT
            </span>
            <h1 className="font-heading text-4xl sm:text-5xl text-tbc-navy mb-4">
              News & Stories
            </h1>
            <p className="text-gray-600 font-body text-lg max-w-2xl mx-auto">
              Stay informed with the latest news, success stories, and insights from the Black business community.
            </p>
          </div>

          {/* Search Bar + Read Later link */}
          <div className="mt-8 max-w-xl mx-auto flex items-center gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <Input
                type="text"
                placeholder="Search articles..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-12 h-12 rounded-full"
                data-testid="news-search"
              />
            </div>
            <Link
              to="/news/saved"
              className="h-12 px-4 rounded-full bg-white border border-tbc-navy/15 text-tbc-navy inline-flex items-center gap-1.5 hover:border-tbc-navy transition-colors whitespace-nowrap"
              data-testid="news-read-later-link"
            >
              <Bookmark className="w-4 h-4" strokeWidth={2} />
              <span className="hidden sm:inline">Read Later</span>
            </Link>
          </div>
        </div>
      </section>

      {/* Category Filter */}
      <section className="py-4 border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-2 overflow-x-auto pb-2">
            {newsCategories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-4 py-2 rounded-full font-body text-sm whitespace-nowrap transition-colors ${
                  selectedCategory === category
                    ? "bg-tbc-navy text-white"
                    : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                }`}
                data-testid={`category-${category.toLowerCase().replace(' ', '-')}`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Articles */}
      {featuredArticles.length > 0 && selectedCategory === "All" && (
        <section className="py-12 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="font-heading text-2xl text-tbc-navy mb-6 flex items-center gap-2">
              <TrendingUp className="w-6 h-6 text-tbc-coral" />
              Featured Stories
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {featuredArticles.map((article) => (
                <ArticleLink
                  article={article}
                  key={article.article_id}
                  className="group relative bg-white rounded-2xl overflow-hidden border border-gray-100 hover:shadow-xl transition-shadow block"
                  testid={`featured-${article.article_id}`}
                >
                  <div className="aspect-video relative overflow-hidden bg-tbc-navy/90">
                    {(article.image_url || article.image) ? (
                      <img
                        src={article.image_url || article.image}
                        alt={article.title}
                        loading="lazy"
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        onError={(e) => {
                          // Broken/hotlink-blocked source → fall back to placeholder
                          e.currentTarget.style.display = "none";
                        }}
                      />
                    ) : (
                      <div className="absolute inset-0 flex items-center justify-center">
                        <BookOpen className="w-10 h-10 text-tbc-gold/40" />
                      </div>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-tbc-navy/80 via-transparent to-transparent"></div>
                    <div className="absolute top-3 right-3 z-10">
                      <SaveArticleButton
                        articleId={article.article_id}
                        initialSaved={!!savedMap[article.article_id]}
                      />
                    </div>
                    <div className="absolute bottom-0 left-0 right-0 p-6">
                      <span className="inline-block px-3 py-1 bg-tbc-coral text-white text-xs font-medium rounded-full mb-3">
                        {article.category}
                      </span>
                      <h3 className="font-heading text-2xl text-white mb-2">
                        {article.title}
                      </h3>
                      <p className="text-white/80 text-sm font-body line-clamp-2">
                        {article.excerpt}
                      </p>
                    </div>
                  </div>
                </ArticleLink>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* All Articles */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-heading text-2xl text-tbc-navy mb-6 flex items-center gap-2">
            <BookOpen className="w-6 h-6 text-tbc-gold" />
            {selectedCategory === "All" ? "Latest Articles" : selectedCategory}
          </h2>

          {(selectedCategory === "All" ? regularArticles : filteredArticles).length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {(selectedCategory === "All" ? regularArticles : filteredArticles).map((article) => (
                <ArticleLink
                  article={article}
                  key={article.article_id}
                  className="group bg-white rounded-xl overflow-hidden border border-gray-100 hover:shadow-lg transition-shadow block"
                  testid={`article-${article.article_id}`}
                >
                  <div className="aspect-video relative overflow-hidden bg-tbc-cream">
                    {(() => {
                      // Image fallback chain: article's own image → branded
                      // category placeholder → BookOpen icon (only if the
                      // category placeholder itself fails to load).
                      const primary = article.image_url || article.image;
                      const placeholder = heroImageForCategory(article.category);
                      const src = primary || placeholder;
                      return src ? (
                        <img
                          src={src}
                          alt={article.title}
                          loading="lazy"
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          onError={(e) => {
                            // If the article's own image 404s, drop to the
                            // category placeholder; if that also fails, hide.
                            if (placeholder && e.currentTarget.src !== placeholder) {
                              e.currentTarget.src = placeholder;
                            } else {
                              e.currentTarget.style.display = "none";
                            }
                          }}
                        />
                      ) : (
                        <div className="absolute inset-0 flex items-center justify-center bg-tbc-navy/90">
                          <BookOpen className="w-8 h-8 text-tbc-gold/40" />
                        </div>
                      );
                    })()}
                    <div className="absolute top-4 left-4">
                      <span className="px-3 py-1 bg-white/90 backdrop-blur-sm text-tbc-navy text-xs font-medium rounded-full">
                        {article.category}
                      </span>
                    </div>
                    <div className="absolute top-3 right-3">
                      <SaveArticleButton
                        articleId={article.article_id}
                        initialSaved={!!savedMap[article.article_id]}
                      />
                    </div>
                  </div>
                  <div className="p-5">
                    <h3 className="font-heading text-xl text-tbc-navy mb-2 group-hover:text-tbc-coral transition-colors line-clamp-2">
                      {article.title}
                    </h3>
                    <p className="text-gray-600 text-sm font-body line-clamp-2 mb-4">
                      {article.excerpt}
                    </p>
                    <div className="flex items-center justify-between text-xs text-gray-500 font-body">
                      <div className="flex items-center gap-4">
                        <span className="flex items-center gap-1">
                          <User className="w-3 h-3" />
                          {article.author}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {getReadTime(article)}
                        </span>
                      </div>
                      <span>{formatDate(getDate(article))}</span>
                    </div>
                  </div>
                </ArticleLink>
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <BookOpen className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="font-heading text-xl text-tbc-navy mb-2">No articles found</h3>
              <p className="text-gray-500 font-body">Try a different search or category</p>
            </div>
          )}
        </div>
      </section>

      {/* Newsletter CTA */}
      <section className="py-16 bg-tbc-navy">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <Megaphone className="w-12 h-12 text-tbc-gold mx-auto mb-4" />
          <h2 className="font-heading text-3xl text-white mb-4">
            Stay in the Loop
          </h2>
          <p className="text-gray-300 font-body mb-8 max-w-xl mx-auto">
            Subscribe to our newsletter for the latest news, business tips, and community updates delivered to your inbox.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
            <Input
              type="email"
              placeholder="Enter your email"
              className="h-12 rounded-full bg-white flex-1"
              data-testid="newsletter-email"
            />
            <Button
              className="bg-tbc-coral hover:bg-tbc-coral/90 text-white rounded-full px-8 h-12"
              data-testid="newsletter-subscribe"
            >
              Subscribe
            </Button>
          </div>
        </div>
      </section>

      {/* Advertise CTA */}
      <section className="py-16 bg-tbc-cream">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-2xl p-8 md:p-12 flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <h2 className="font-heading text-2xl text-tbc-navy mb-2">
                Advertise With Us
              </h2>
              <p className="text-gray-600 font-body max-w-lg">
                Reach thousands of engaged community members. Promote your business, event, or service to our growing audience.
              </p>
            </div>
            <Button
              className="bg-tbc-gold hover:bg-tbc-gold/90 text-tbc-navy rounded-full px-8 py-6 whitespace-nowrap"
              data-testid="advertise-cta"
            >
              Learn More
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
